import { Plane } from '@/modules/Editor/utils/raster/Plane'
import { MedicalVolumePlane } from '@/modules/Editor/MedicalMetadata'
import type { VoxelRasterDimensions } from '@/modules/Editor/models/raster/VoxelRaster'
import type { Range3D } from '@/modules/Editor/types'
import type { InvalidatedRasterRegion } from '@/modules/Editor/models/raster/Raster'

/**
 * Map to list all the dimensions transformations required to inspect a buffer, depending on
 * the primary plane and the inspection plane
 */
export const PlaneToRasterPlane: Record<
  MedicalVolumePlane,
  Record<Plane, Record<keyof VoxelRasterDimensions, keyof VoxelRasterDimensions>>
> = {
  [MedicalVolumePlane.SAGITTAL]: {
    [Plane.Y]: {
      width: 'depth',
      height: 'width',
      depth: 'height',
    },
    [Plane.X]: {
      width: 'depth',
      height: 'height',
      depth: 'width',
    },
    [Plane.Z]: {
      width: 'width',
      height: 'height',
      depth: 'depth',
    },
  },
  [MedicalVolumePlane.CORONAL]: {
    [Plane.Y]: {
      width: 'width',
      height: 'depth',
      depth: 'height',
    },
    [Plane.X]: {
      width: 'depth',
      height: 'height',
      depth: 'width',
    },
    [Plane.Z]: {
      width: 'width',
      height: 'height',
      depth: 'depth',
    },
  },
  [MedicalVolumePlane.AXIAL]: {
    [Plane.Y]: {
      width: 'width',
      height: 'depth',
      depth: 'height',
    },
    [Plane.X]: {
      width: 'height',
      height: 'depth',
      depth: 'width',
    },
    [Plane.Z]: {
      width: 'width',
      height: 'height',
      depth: 'depth',
    },
  },
}

export const DimensionToAxisMap: Record<keyof VoxelRasterDimensions, Plane> = {
  width: Plane.X,
  height: Plane.Y,
  depth: Plane.Z,
}

export type PrimaryPlaneToPlaneDataMap = VoxelRasterDimensions & {
  getIndex: (x: number, y: number, z: number) => number
}

/**
 * Given a plane 3D dimensions, returns the transformed dimensions from the primary plain
 * perspective, and a helper to get the voxel raster buffer index for a x, y, z point
 */
export const primaryPlaneToRasterPlaneData = (
  primaryPlane: MedicalVolumePlane = MedicalVolumePlane.AXIAL,
  plane: Plane,
  width: number,
  height: number,
  depth: number,
): PrimaryPlaneToPlaneDataMap => {
  const size = width * height
  const originalDimensions: VoxelRasterDimensions = { width, height, depth }
  const transformeDimensionsMap = PlaneToRasterPlane[primaryPlane][plane]
  const transformedDimensions = {
    width: originalDimensions[transformeDimensionsMap.width],
    height: originalDimensions[transformeDimensionsMap.height],
    depth: originalDimensions[transformeDimensionsMap.depth],
  }

  if (primaryPlane === MedicalVolumePlane.SAGITTAL) {
    if (plane === Plane.Y) {
      return {
        ...transformedDimensions,
        getIndex: (x: number, y: number, z: number): number => x * size + z * width + y,
      }
    }
    if (plane === Plane.X) {
      return {
        ...transformedDimensions,
        getIndex: (x: number, y: number, z: number): number => x * size + y * width + z,
      }
    }
  }

  if (primaryPlane === MedicalVolumePlane.CORONAL) {
    if (plane === Plane.X) {
      return {
        ...transformedDimensions,
        getIndex: (x: number, y: number, z: number): number => x * size + y * width + z,
      }
    }
  }

  // Acquired Axial - default
  if (plane === Plane.Y) {
    return {
      ...transformedDimensions,
      getIndex: (x: number, y: number, z: number): number => y * size + z * width + x,
    }
  }

  if (plane === Plane.X) {
    return {
      ...transformedDimensions,
      getIndex: (x: number, y: number, z: number): number => y * size + x * width + z,
    }
  }

  if (plane === Plane.Z) {
    return {
      ...transformedDimensions,
      getIndex: (x: number, y: number, z: number): number => z * size + y * width + x,
    }
  }

  return {
    ...transformedDimensions,
    getIndex: (x: number, y: number, z: number): number => z * size + y * width + x,
  }
}

/**
 * Given a 3D range, it returns the 2D range for the passed plane, considering the primary plane
 * necessary transformations
 */
export const range3DToPlaneRange2D = (
  primaryPlane: MedicalVolumePlane,
  plane: Plane,
  range3D: Range3D,
): InvalidatedRasterRegion => {
  // Get the plane to primary plane transformation map
  const planeTransformationMap = PlaneToRasterPlane[primaryPlane][plane]
  // Get the axis (X, Y, Z) for the transformation
  const xRangeAxis = DimensionToAxisMap[planeTransformationMap.width]
  const yRangeAxis = DimensionToAxisMap[planeTransformationMap.height]

  // Find the range3D keys
  const minXKey = `min${xRangeAxis}` as keyof Range3D
  const maxXKey = `max${xRangeAxis}` as keyof Range3D
  const minYKey = `min${yRangeAxis}` as keyof Range3D
  const maxYKey = `max${yRangeAxis}` as keyof Range3D

  return {
    xMin: range3D[minXKey],
    xMax: range3D[maxXKey],
    yMin: range3D[minYKey],
    yMax: range3D[maxYKey],
  }
}
